import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import masterTest from '../data/masterTest.json';
import ScoreCard from '../components/ScoreCard';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import './TestPage.css';

const PracticeTestPage = () => {
  const { section } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    document.body.classList.add('notranslate');
    return () => {
      document.body.classList.remove('notranslate');
    };
  }, []);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [showScore, setShowScore] = useState(false);
  const [score, setScore] = useState(0);
  const [questionPage, setQuestionPage] = useState(location.state?.page || 0);
  const [showInstructions, setShowInstructions] = useState(true);

  const [isReadingPhase, setIsReadingPhase] = useState(true);

  const getInitialTime = () => {
    switch (section) {
      case 'typing':
        return 60;
      case 'sentenceCompletion':
        return 25; // Realigned from 15s to 25s
      case 'fillBlanks':
        return 30; // Realigned from 15s to 30s
      case 'emailWriting':
        return 540; // Realigned from 300s to 540s (9 mins)
      case 'passageReconstruction':
        return isReadingPhase ? 30 : 90; // 30s read, 90s write
      default:
        return 30; // for jumbledWords
    }
  };

  const getSectionTimeText = () => {
    switch (section) {
      case 'typing':
        return '60 seconds';
      case 'sentenceCompletion':
        return '25 seconds per question';
      case 'fillBlanks':
        return '30 seconds per question';
      case 'emailWriting':
        return '9 minutes';
      case 'passageReconstruction':
        return '30 seconds of reading and 90 seconds of writing per passage';
      default:
        return '30 seconds per question';
    }
  };

  const formatTime = (seconds) => {
    if (section === 'emailWriting' || section === 'passageReconstruction' || section === 'typing') {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    }
    return `${seconds}s`;
  };

  const [timeLeft, setTimeLeft] = useState(getInitialTime());
  const [showDetailedScore, setShowDetailedScore] = useState(false);
  const inputRef = useRef(null);
  
  const [loadingAccess, setLoadingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);

  useEffect(() => {
    document.body.classList.add('test-page-active');
    return () => {
      document.body.classList.remove('test-page-active');
    };
  }, []);

  useEffect(() => {
    const checkAccess = async () => {
      // Set 1 (page 0) is always free
      if (questionPage === 0) {
        setLoadingAccess(false);
        setHasAccess(true);
        return;
      }

      const authInstance = getAuth();
      // Wait for auth to resolve if loading
      const unsubscribe = authInstance.onAuthStateChanged(async (currentUser) => {
        if (!currentUser) {
          setHasAccess(false);
          navigate('/login');
          setLoadingAccess(false);
          return;
        }

        try {
          const docRef = doc(db, 'users', currentUser.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            const isFullPremium = data.paidTests === true || data.hasPaid === true || data.isPremium === true;
            const isSectionUnlocked = data.unlockedSections?.[section] === true;
            
            if (isFullPremium || isSectionUnlocked) {
              setHasAccess(true);
            } else {
              setHasAccess(false);
              navigate('/pricing');
            }
          } else {
            setHasAccess(false);
            navigate('/pricing');
          }
        } catch (e) {
          console.error("Error checking access:", e);
          setHasAccess(false);
          navigate('/pricing');
        }
        setLoadingAccess(false);
      });

      return () => unsubscribe();
    };

    checkAccess();
  }, [section, questionPage, navigate]);

  const loadQuestions = () => {
    const testIndexStr = (questionPage + 1).toString();
    const testData = masterTest[testIndexStr] || masterTest["1"] || {};
    const newQuestions = testData[section] || [];
    setQuestions(newQuestions);
    setCurrentQuestionIndex(0);
    setIsReadingPhase(true);
    setUserAnswers({});
    setShowScore(false);
    setShowInstructions(true);
    setShowDetailedScore(false);
    setScore(0);
    setTimeLeft(section === 'passageReconstruction' ? 30 : getInitialTime());
  };

  useEffect(() => {
    if (!loadingAccess && hasAccess) {
      loadQuestions();
    }
  }, [section, questionPage, loadingAccess, hasAccess]);

  const activeAudioRef = useRef(null);

  const stopAllSpeech = () => {
    if (activeAudioRef.current) {
      activeAudioRef.current.pause();
      activeAudioRef.current.onended = null;
      activeAudioRef.current.onerror = null;
      activeAudioRef.current = null;
    }
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  };

  const speakInstructionSynth = (text, onEnd) => {
    if (!text) { if (onEnd) onEnd(); return; }
    stopAllSpeech();
    if ('speechSynthesis' in window) {
      setTimeout(() => {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'en-US';
        utterance.rate = 0.95;
        utterance.pitch = 1.0;
        
        const voices = window.speechSynthesis.getVoices();
        const preferredVoice = voices.find(voice =>
          (voice.name.includes('Google') && voice.lang === 'en-US') ||
          (voice.name.includes('Samantha') && voice.lang === 'en-US')
        );
        if (preferredVoice) {
          utterance.voice = preferredVoice;
        }

        utterance.onend = () => {
          if (onEnd) onEnd();
        };
        utterance.onerror = () => {
          if (onEnd) onEnd();
        };

        window.speechSynthesis.speak(utterance);
      }, 50);
    } else {
      if (onEnd) onEnd();
    }
  };

  const playAudioOrSpeak = (audioUrl, fallbackText, onEnd) => {
    stopAllSpeech();
    if (!audioUrl) {
      speakInstructionSynth(fallbackText, onEnd);
      return;
    }

    const audio = new Audio(audioUrl);
    activeAudioRef.current = audio;

    audio.onended = () => {
      activeAudioRef.current = null;
      if (onEnd) onEnd();
    };

    audio.onerror = (e) => {
      console.warn(`[Audio Player] MP3 failed at ${audioUrl}, falling back:`, e);
      activeAudioRef.current = null;
      speakInstructionSynth(fallbackText, onEnd);
    };

    audio.play().catch(e => {
      console.warn(`[Audio Player] Autoplay prevented for ${audioUrl}, falling back:`, e);
      activeAudioRef.current = null;
      speakInstructionSynth(fallbackText, onEnd);
    });
  };

  useEffect(() => {
    if (showInstructions && !loadingAccess && hasAccess) {
      const timeText = getSectionTimeText();
      const instructionText = `This section is ${section.replace(/([A-Z])/g, ' $1').trim()}. You will have ${timeText}. Click 'Start Section' to begin.`;
      
      let instAudioUrl = '';
      if (section === 'sentenceCompletion') instAudioUrl = '/audio/instructions/inst_part_b_repeat.mp3';
      else if (section === 'fillBlanks') instAudioUrl = '/audio/instructions/inst_part_b_repeat.mp3';

      playAudioOrSpeak(instAudioUrl, instructionText);
    }
    return () => {
      stopAllSpeech();
    };
  }, [showInstructions, section, loadingAccess, hasAccess]);

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setIsReadingPhase(true);
      setTimeLeft(section === 'passageReconstruction' ? 30 : getInitialTime());
    } else {
      handleSubmit();
    }
  };

  const handleNextAction = () => {
    if (section === 'passageReconstruction' && isReadingPhase) {
      setIsReadingPhase(false);
      setTimeLeft(90);
    } else {
      handleNextQuestion();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && !showInstructions && !showScore) {
        if (section !== 'typing' && section !== 'emailWriting' && section !== 'passageReconstruction') {
          handleNextQuestion();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentQuestionIndex, showInstructions, showScore, section]);

  useEffect(() => {
    if (!showInstructions && !showScore) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            if (section === 'passageReconstruction' && isReadingPhase) {
              setIsReadingPhase(false);
              return 90;
            } else {
              handleNextQuestion();
              return 0;
            }
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [currentQuestionIndex, showInstructions, showScore, isReadingPhase, section]);

  useEffect(() => {
    if (!showInstructions && !isReadingPhase && inputRef.current) {
      inputRef.current.focus();
    }
  }, [currentQuestionIndex, showInstructions, isReadingPhase]);

  const handleAnswerChange = (questionId, answer) => {
    setUserAnswers({ ...userAnswers, [questionId]: answer });
  };

  const handleSubmit = () => {
    navigate('/result', {
      state: {
        isPractice: true,
        section,
        questions,
        answers: userAnswers,
        timeLeft
      }
    });
  };

  const handleTakeAnotherTest = () => {
    setQuestionPage(prevPage => prevPage + 1);
  };
  
  const handleExitTest = () => {
    if (window.confirm('Are you sure you want to exit the test? Your progress will be lost.')) {
      navigate('/practice');
    }
  };

  const getQuestionText = (question) => {
    if (!question) return '';
    switch (section) {
        case 'jumbledWords':
            return question.jumbled.replace(/\//g, ' ');
        case 'typing':
            return question.paragraph;
        case 'emailWriting':
            return question.prompt;
        case 'passageReconstruction':
            return isReadingPhase 
              ? question.sentences 
              : "Reconstruct the passage in your own words. Remember to include as many details as possible.";
        default:
            return question.question;
    }
  }

  const renderQuestion = (question) => {
    switch (section) {
      case 'fillBlanks':
        return (
          <div className="space-y-2 mt-4 text-left">
            {question.options.map((option, i) => (
              <label key={i} className="flex items-center space-x-3 p-2 rounded-md hover:bg-white/20">
                <input
                  ref={i === 0 ? inputRef : null}
                  type="radio"
                  value={option}
                  checked={userAnswers[question.id] === option}
                  onChange={() => handleAnswerChange(question.id, option)}
                  className="h-4 w-4 text-green-300 bg-transparent border-white/50 focus:ring-green-300"
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );
      case 'jumbledWords':
      case 'sentenceCompletion':
        return (
          <input
            ref={inputRef}
            type="text"
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            value={userAnswers[question.id] || ''}
            className="test-input"
          />
        );
      case 'typing':
      case 'emailWriting':
        return (
          <textarea
            ref={inputRef}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            value={userAnswers[question.id] || ''}
            className="test-textarea h-48"
            placeholder="Start writing..."
          />
        );
      case 'passageReconstruction':
        return isReadingPhase ? (
          <div className="p-4 bg-slate-900/50 rounded-xl border border-white/10 text-center italic text-gray-300">
            Read the passage carefully. You cannot type during this 30-second reading phase.
          </div>
        ) : (
          <textarea
            ref={inputRef}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            value={userAnswers[question.id] || ''}
            className="test-textarea h-48"
            placeholder="Start writing..."
          />
        );
      default:
        return <p>Unsupported question type.</p>;
    }
  };

  if (loadingAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-900 text-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Verifying access permission...</p>
        </div>
      </div>
    );
  }

  if (showInstructions) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="test-container-glass max-w-md">
          <button onClick={handleExitTest} className="test-btn-exit-corner">
            &times;
          </button>
          <h2 className="test-title">{section.replace(/([A-Z])/g, ' $1').trim()}</h2>
          <div className="test-question-area">
            <p className="test-subtitle">
              This section is {section.replace(/([A-Z])/g, ' $1').trim()}. You will have {getSectionTimeText()}. Click 'Start Section' to begin.
            </p>
          </div>
          <button onClick={() => setShowInstructions(false)} className="test-btn test-btn-primary">Start Section</button>
        </div>
      </div>
    );
  }

  if (showScore) {
    if (showDetailedScore) {
        return (
          <div className="min-h-screen flex items-center justify-center p-4">
            <div className="test-container-glass">
                 <ScoreCard score={score} total={questions.length} questions={questions} userAnswers={userAnswers} section={section} />
                <div className="flex justify-center mt-4 space-x-4">
                    <button
                        onClick={() => setShowDetailedScore(false)}
                        className="test-btn test-btn-secondary"
                    >
                        Back to Summary
                    </button>
                    <button
                        onClick={handleTakeAnotherTest}
                        className="test-btn test-btn-primary"
                    >
                        Take Another Test
                    </button>
                    <button
                        onClick={() => navigate('/practice')}
                        className="test-btn test-btn-secondary"
                    >
                        Back to Practice Hub
                    </button>
                </div>
            </div>
          </div>
        );
    }
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="test-container-glass bg-green-900 text-white">
            <h2 className="test-title">Test Complete!</h2>
            <p className="test-subtitle">
              {section === 'typing'
                ? `Your score is ${score} WPM.`
                : (section === 'emailWriting' || section === 'passageReconstruction')
                ? 'You have completed the section.'
                : `You scored ${score} out of ${questions.length}.`
              }
            </p>
                        <div className="flex justify-center mt-4 space-x-4">
                            <button
                                onClick={() => setShowDetailedScore(true)}
                                className="test-btn test-btn-primary"
                            >
                                View Detailed Scorecard
                            </button>
                            <button
                                onClick={handleTakeAnotherTest}
                                className="test-btn test-btn-secondary"
                            >
                                Take Another Test
                            </button>
                            <button
                                onClick={() => navigate('/practice')}
                                className="test-btn test-btn-secondary"
                            >
                                Back to Practice Hub
                            </button>
                        </div>        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  
  return (
    <>
      <div className="min-h-screen flex items-center justify-center p-4">
        {questions.length > 0 && currentQuestion ? (
          <div className="test-container-glass">
             <button onClick={handleExitTest} className="test-btn-exit-corner">
              &times;
            </button>
            <h2 className="test-title">{section.replace(/([A-Z])/g, ' $1').trim()}</h2>
            <p className="test-subtitle">Question {currentQuestionIndex + 1} of {questions.length}</p>
            <div className="timer-display-circular">
              <div className="timer-text-circular">{formatTime(timeLeft)}</div>
            </div>
            <div className="test-question-area">
                <p>{getQuestionText(currentQuestion)}</p>
            </div>
            {renderQuestion(currentQuestion)}
            <button
                onClick={handleNextAction}
                className="test-btn test-btn-primary"
              >
                {section === 'passageReconstruction' && isReadingPhase 
                  ? 'Start Writing' 
                  : (currentQuestionIndex < questions.length - 1 ? 'Next' : 'Submit')}
              </button>
          </div>
        ) : (
          <p>Loading questions or no more questions available...</p>
        )}
      </div>
    </>
  );
};

export default PracticeTestPage;